/** Method status and experience badges — SPEC-2 §4. */

import { Archive, BadgeCheck, TriangleAlert } from "lucide-react";

import type { MethodStatus } from "@/api/types";
import { Mark } from "@/components/ui/Status";
import { pluralRu } from "@/lib/format";

const ICON = "h-3 w-3 shrink-0";

export const METHOD_STATUS_LABEL: Record<MethodStatus, string> = {
  draft: "черновик",
  proven: "проверен",
  needs_review: "нужен пересмотр",
  archived: "в архиве",
};

/**
 * A method's state is a property of the knowledge, not a work status (Spec 020
 * D3), so it is a `Mark`: a quiet word with an icon, no pill and no status
 * colour. The icons keep them apart — `proven` is the one claim backed by
 * evidence the server checked; `needs_review` warns about knowledge you might
 * otherwise trust; `archived` is dimmed, still findable, never competing.
 */
export function MethodStatusBadge({ status }: { status: MethodStatus }) {
  switch (status) {
    case "proven":
      return (
        <Mark className="text-p-text" icon={<BadgeCheck className={ICON} aria-hidden="true" />}>
          {METHOD_STATUS_LABEL.proven}
        </Mark>
      );
    case "needs_review":
      return (
        <Mark className="text-p-text" icon={<TriangleAlert className={ICON} aria-hidden="true" />}>
          {METHOD_STATUS_LABEL.needs_review}
        </Mark>
      );
    case "archived":
      return (
        <Mark className="opacity-60" icon={<Archive className={ICON} aria-hidden="true" />}>
          {METHOD_STATUS_LABEL.archived}
        </Mark>
      );
    case "draft":
    default:
      return <Mark>{METHOD_STATUS_LABEL.draft}</Mark>;
  }
}

/**
 * "v3 · применён 4× · 3 успешно" — the line that turns a method from a claim
 * into a track record. Rendered as plain `--hint` text rather than badges: it
 * is context for the title above it, not a status of its own.
 */
export function MethodExperienceLine({
  versionNumber,
  applications,
  success,
}: {
  versionNumber?: number | null;
  applications: number;
  success: number;
}) {
  const parts: string[] = [];
  if (versionNumber != null) parts.push(`v${versionNumber}`);
  if (applications > 0) {
    parts.push(`применён ${applications}×`);
    // Zero successes is worth saying out loud: "applied four times, never
    // worked" is the most useful thing this line can tell anyone.
    parts.push(`${success} ${pluralRu(success, "успешно", "успешно", "успешно")}`);
  } else {
    parts.push("ещё не применялся");
  }
  return <p className="text-meta text-hint">{parts.join(" · ")}</p>;
}
